// H-27, H-28: closing the round.
//
// Closing is always manual, never on a deadline, and it needs a reason (L-3).
// It also sends the unsuccessful email to everyone who was not hired, so the
// owner PREVIEWS that email here before they close: it is one global template
// they cannot edit (H-28), and the preview is their chance to see what is about
// to go out in their organisation's name.

import React, { useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import {
  ActionButton,
  BODY,
  DANGER,
  FONT,
  LINE,
  Note,
  SURFACE,
  TEXT,
  TEXT_2,
  WELL
} from '../capability-setup/designSystem';
import { RationaleField } from './hiringParts';
import type { CopyBlock, HiringRoundView } from '../../../../api/hiring';

const CloseRoundDialog: React.FC<{
  open: boolean;
  round: HiringRoundView;
  /** How many will get the unsuccessful email, so the number is never a surprise. */
  emailCount: number;
  hiredCount: number;
  tooltip: CopyBlock | null;
  busy?: boolean;
  error?: string | null;
  onClose: (rationale: string) => void;
  onCancel: () => void;
}> = ({ open, round, emailCount, hiredCount, tooltip, busy = false, error, onClose, onCancel }) => {
  const [rationale, setRationale] = useState('');
  const short = rationale.trim().length < 10;
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' } }}
    >
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>
            Close {round.name}
          </Typography>
          <Typography sx={{ ...BODY, mt: '4px' }}>
            The round becomes read-only. You can still open every candidate&apos;s analysis and transcript, and delete a
            candidate&apos;s data if they ask.
          </Typography>
        </Box>

        {hiredCount === 0 && (
          <Note>Nobody has been hired in this round yet. To hire after closing, you&apos;d reopen the round first.</Note>
        )}

        <Box
          component="ul"
          sx={{
            m: 0,
            p: '14px 14px 14px 30px',
            borderRadius: '4px',
            bgcolor: WELL,
            border: `1px solid ${LINE}`,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          {[
            'Pending invites stop working',
            'Anyone part-way through sees "this assessment has closed"',
            `${emailCount} ${emailCount === 1 ? 'candidate' : 'candidates'} will receive the polite 'Unsuccessful' email`,
            `These candidates will be informed that their data is kept for ${round.retentionMonths} months`,
            'These candidates will be informed how to ask questions or exercise their rights',
            'The other Team Owners for this team will be told the round has closed',
            `The ${round.retentionMonths} month retention clock starts now`
          ].map((line) => (
            <Typography key={line} component="li" sx={{ ...BODY, fontSize: 12.5, color: TEXT_2 }}>
              {line}
            </Typography>
          ))}
        </Box>

        <RationaleField
          value={rationale}
          onChange={setRationale}
          tooltip={tooltip}
          label="Why are you closing this round?"
        />

        {error && (
          <Typography sx={{ ...BODY, color: DANGER }} role="alert">
            {error}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <ActionButton onClick={onCancel}>Cancel</ActionButton>
          <ActionButton
            primary
            disabled={short || busy}
            reason={short ? 'Say briefly why you are closing it' : undefined}
            onClick={() => onClose(rationale.trim())}
          >
            {busy ? 'Closing...' : 'Close Round'}
          </ActionButton>
        </Box>
      </Box>
    </Dialog>
  );
};

/** Reopening a closed round, so an owner can still hire from it. It sends nobody an email. */
export const ReopenRoundDialog: React.FC<{
  open: boolean;
  round: HiringRoundView;
  busy?: boolean;
  error?: string | null;
  onReopen: () => void;
  onCancel: () => void;
}> = ({ open, round, busy = false, error, onReopen, onCancel }) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth="sm"
    fullWidth
    PaperProps={{ sx: { bgcolor: SURFACE, border: `1px solid ${LINE}`, borderRadius: '8px' } }}
  >
    <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 600, color: TEXT }}>Reopen {round.name}</Typography>
      <Box
        component="ul"
        sx={{
          m: 0,
          p: '14px 14px 14px 30px',
          borderRadius: '4px',
          bgcolor: WELL,
          border: `1px solid ${LINE}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        {[
          'Candidates moved to Unsuccessful by closing can be hired or removed again',
          'Anyone part-way through can finish their assessment',
          'Expired invites stay expired until you resend them',
          'No emails are sent. Only a candidate you hire is emailed',
          'Anyone already told how long their data is kept keeps that same date'
        ].map((line) => (
          <Typography key={line} component="li" sx={{ ...BODY, fontSize: 12.5, color: TEXT_2 }}>
            {line}
          </Typography>
        ))}
      </Box>
      {error && (
        <Typography sx={{ ...BODY, color: DANGER }} role="alert">
          {error}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <ActionButton onClick={onCancel}>Cancel</ActionButton>
        <ActionButton primary disabled={busy} onClick={onReopen}>
          {busy ? 'Reopening...' : 'Reopen Round'}
        </ActionButton>
      </Box>
    </Box>
  </Dialog>
);

export default CloseRoundDialog;
